import { useState } from "react";
import { Apple, Download, Loader2 } from "lucide-react";
import { newestToolboxRelease } from "@ttc-spectree/toolbox-contract/contracts";
import { Button } from "@/components/ui/button";
import { useToolboxLatest } from "@/hooks/queries/use-toolbox-latest";
import { usePageTitle } from "@/hooks/use-page-title";
import { ApiError } from "@/lib/api/client";
import { installerSizeMb, saveToolboxInstaller, type ToolboxReleaseFile } from "@/lib/api/toolbox-updates";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/** "Oct 24", or "Oct 24, 2025" when it is not this year. Exported for tests. */
export function formatReleaseDate(releaseDate: string, now: Date = new Date()): string | null {
  const date = new Date(releaseDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** "0.5.1 · 98 MB · installs for you only, no admin rights". Exported for tests. */
export function windowsMeta(release: ToolboxReleaseFile): string {
  const size = installerSizeMb(release.sizeBytes);
  return [release.version, size > 0 ? `${String(size)} MB` : null, "installs for you only, no admin rights"]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/** The lead sentence under the title, naming the newer release (newestToolboxRelease, as the API picks its note). */
function leadText(latest: ToolboxReleaseFile | undefined): string {
  const base = "The desktop app for grooming and writing Azure DevOps items.";
  if (!latest) return base;
  const date = formatReleaseDate(latest.releaseDate);
  return date
    ? `${base} Latest version ${latest.version}, released ${date}.`
    : `${base} Latest version ${latest.version}.`;
}

/** Why the download list could not be read, in plain words. */
function loadErrorText(error: unknown): string {
  if (error instanceof ApiError && error.response.status === 503) {
    return "Downloads aren't set up on this SpecTree yet. Ask your SpecTree admin.";
  }
  return "Couldn't read the latest version. Reload the page to try again.";
}

/** One download button: fetches the installer with the person's token, then saves it. */
function useInstallerDownload() {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function download(fileName: string) {
    setDownloading(fileName);
    setError(null);
    try {
      await saveToolboxInstaller(fileName);
    } catch {
      setError("The download didn't finish. Try again; if it keeps failing, ask your SpecTree admin.");
    } finally {
      setDownloading(null);
    }
  }

  return { downloading, error, download };
}

/** The "Windows protected your PC" box, drawn so the person recognises it. */
function SmartScreenIllustration() {
  return (
    <div
      role="img"
      aria-label='The blue "Windows protected your PC" box, with More info and Run anyway'
      className="w-[300px] shrink-0 rounded-[4px] bg-[#1F4E8C] px-4 py-3.5 text-xs leading-[1.45] text-white"
      data-testid="smartscreen-illustration"
    >
      <div aria-hidden="true">
        <div className="mb-2 text-[15px] font-semibold">Windows protected your PC</div>
        Microsoft Defender SmartScreen prevented an unrecognised app from starting…
        <div className="mt-2.5 underline">More info</div>
        <div className="mt-3.5 text-right">
          <span className="inline-block border border-white px-3.5 py-[5px]">Run anyway</span>
        </div>
      </div>
    </div>
  );
}

const SMARTSCREEN_STEPS = [
  <>
    Run the installer. A blue box says <b>&ldquo;Windows protected your PC&rdquo;</b>.
  </>,
  <>
    Click <b>More info</b>, then <b>Run anyway</b>.
  </>,
  <>The Toolbox opens and walks you through setup.</>,
];

/**
 * `/toolbox/download` (TOOLBOX_DOWNLOAD_PAGE_PATH; epic PI-SPEC-SW-E288,
 * feature PI-SPEC-SW-1468, UX-PM-41, design "Getting Started Mockups" frame
 * 1h). Sign-in required (it sits inside AppLayout). During the unsigned pilot
 * this is where a person gets SpecTree Toolbox the first time and every
 * update after: the Windows installer and the Apple Silicon Mac build from
 * the update feed, and the SmartScreen step shown, not hidden — someone who
 * meets "Windows protected your PC" without warning stops there. The in-app
 * band and the title bar's "Update ready" chip link here.
 */
export function ToolboxDownloadPage() {
  usePageTitle("Get SpecTree Toolbox");
  const { data, isLoading, error } = useToolboxLatest();
  const { downloading, error: downloadError, download } = useInstallerDownload();
  const windows = data?.windows;
  const mac = data?.mac;

  return (
    <div className="container mx-auto flex max-w-[820px] flex-col gap-[18px] px-4 py-8" data-testid="toolbox-download">
      <div className="flex flex-col gap-1.5">
        <h1 className={TOOLBOX_PAGE_TITLE}>Get SpecTree Toolbox</h1>
        <p className="text-sm text-muted-foreground">{leadText(newestToolboxRelease(windows, mac))}</p>
      </div>

      <section className="glass-card flex flex-wrap items-center gap-4 rounded-xl px-6 py-[22px]" aria-label="Downloads">
        {isLoading ? (
          <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} role="status">
            Looking for the latest version…
          </p>
        ) : error ? (
          <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} role="alert" data-testid="toolbox-download-error">
            {loadErrorText(error)}
          </p>
        ) : !windows && !mac ? (
          <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="toolbox-download-none">
            No version has been published yet. Ask your SpecTree admin.
          </p>
        ) : (
          <>
            {windows ? (
              <>
                <Button
                  size="lg"
                  className="h-10 gap-2 rounded-[9px] bg-primary px-[18px] text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                  disabled={downloading !== null}
                  onClick={() => void download(windows.fileName)}
                  data-testid="download-windows"
                >
                  {downloading === windows.fileName ? (
                    <Loader2 className="animate-spin" aria-hidden="true" />
                  ) : (
                    <Download aria-hidden="true" />
                  )}
                  {downloading === windows.fileName ? "Downloading…" : "Download for Windows"}
                </Button>
                <span className={cn(TOOLBOX_TEXT.meta, "min-w-0 flex-1 text-muted-foreground")} data-testid="download-windows-meta">
                  {windowsMeta(windows)}
                </span>
              </>
            ) : (
              <span className={cn(TOOLBOX_TEXT.content, "min-w-0 flex-1 text-muted-foreground")}>
                No Windows version has been published yet.
              </span>
            )}
            {mac && (
              <Button
                variant="outline"
                size="lg"
                className="h-10 gap-2 rounded-[9px] px-4 text-sm font-medium"
                disabled={downloading !== null}
                onClick={() => void download(mac.fileName)}
                title={`${mac.version} · ${String(installerSizeMb(mac.sizeBytes))} MB`}
                data-testid="download-mac"
              >
                {downloading === mac.fileName ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Apple aria-hidden="true" />
                )}
                {downloading === mac.fileName ? "Downloading…" : "Mac (Apple Silicon)"}
              </Button>
            )}
            {downloadError && (
              <p className={cn(TOOLBOX_TEXT.content, "w-full text-destructive")} role="alert">
                {downloadError}
              </p>
            )}
          </>
        )}
      </section>

      <section className="glass-card flex flex-wrap gap-6 rounded-xl px-6 py-[22px]" aria-labelledby="smartscreen-heading">
        <div className="flex min-w-[260px] flex-1 flex-col gap-3">
          <h2 id="smartscreen-heading" className="font-display text-sm font-semibold leading-snug">
            Windows will warn you once. That&rsquo;s expected.
          </h2>
          <ol className="flex flex-col gap-3" data-testid="smartscreen-steps">
            {SMARTSCREEN_STEPS.map((step, index) => (
              <li key={index} className={cn(TOOLBOX_TEXT.content, "grid grid-cols-[26px_minmax(0,1fr)] items-start gap-2.5 leading-normal")}>
                <span
                  className="flex h-[22px] w-[22px] items-center justify-center rounded-full border border-border bg-secondary text-[11px] font-semibold"
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <p className={cn(TOOLBOX_TEXT.meta, "leading-relaxed text-muted-foreground")}>
            The pilot build isn&rsquo;t signed yet, so Windows doesn&rsquo;t recognise its publisher. Each update shows this
            box again until it is.
          </p>
        </div>
        <SmartScreenIllustration />
      </section>
    </div>
  );
}
